import type { BoardData, Item, Port } from '../model/schema.js';

/** Port signals that carry many lanes over one cable (digital snakes, multipin). */
const TRUNK_SIGNALS = new Set<Port['signal']>(['aes', 'dante', 'ethernet', 'analog_multi']);

/** Kinds whose inputs are routed straight to outputs (or to a trunk) by index. */
const PASS_THROUGH_KINDS = new Set<Item['kind']>(['stagebox', 'subcore', 'di']);

/** True for ports carrying multiple lanes (AES50, Dante, multipin audio …). Power is never a trunk. */
export function isTrunk(port: Port): boolean {
  return (
    TRUNK_SIGNALS.has(port.signal) || (port.connector === 'multipin' && port.signal !== 'power')
  );
}

/** Stageboxes, sub-cores, DIs and items with `props.passThrough === true`. */
export function isPassThrough(item: Item): boolean {
  return PASS_THROUGH_KINDS.has(item.kind) || item.props['passThrough'] === true;
}

export interface LanePorts {
  inputs: Port[];
  outputs: Port[];
  trunks: Port[];
}

/** Audio ports of an item grouped for index-based pass-through. Power ports are ignored. */
export function lanePorts(item: Item): LanePorts {
  const audio = (item.ports ?? []).filter((p) => p.signal !== 'power');
  return {
    inputs: audio.filter((p) => p.direction === 'in' && !isTrunk(p)),
    outputs: audio.filter((p) => p.direction === 'out' && !isTrunk(p)),
    trunks: audio.filter(isTrunk),
  };
}

/**
 * Output indices fed by input `k` when there is no trunk: if the outputs are a multiple of the
 * inputs (DI out + thru), input k feeds every output j with j mod n = k; otherwise output k.
 */
export function outputsForInput(nIn: number, nOut: number, k: number): number[] {
  if (nOut >= nIn && nOut % nIn === 0) {
    return Array.from({ length: nOut / nIn }, (_, m) => k + m * nIn);
  }
  return k < nOut ? [k] : [];
}

/** Inverse of {@link outputsForInput}. */
export function inputForOutput(nIn: number, nOut: number, j: number): number | undefined {
  if (nOut >= nIn && nOut % nIn === 0) return j % nIn;
  return j < nIn ? j : undefined;
}

/** Looks up an item's port. */
export function findPort(data: BoardData, itemId: string, portId: string): Port | undefined {
  return data.items[itemId]?.ports?.find((p) => p.id === portId);
}

/** Human readable item name: label, else model, else kind. */
export function itemName(item: Item): string {
  return item.label || item.model || item.kind;
}

/**
 * Resolves an informal patch text like `SB1:5`, `SB1/5` or `SB1 5` to the n-th input of the item
 * labelled `SB1` (case-insensitive). Returns undefined if nothing matches.
 */
export function resolvePatch(
  data: BoardData,
  text: string,
): { itemId: string; portId: string } | undefined {
  const m = /^(.+?)\s*[:/ ]\s*(\d+)$/.exec(text.trim());
  if (m === null) return undefined;
  const label = m[1]!.toLowerCase();
  const index = Number(m[2]) - 1;
  for (const item of Object.values(data.items)) {
    if (item.label.toLowerCase() !== label) continue;
    const input = lanePorts(item).inputs[index];
    if (input !== undefined) return { itemId: item.id, portId: input.id };
  }
  return undefined;
}
